import styles from "../../../../styles/pages/Lead/LeadManagement/Chat/LeadChatPage.module.css";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { ThemeContext } from "../../../../context/ThemeContext";
import { LangContext } from "../../../../context/LangContext";

import { LoadingCircle } from "../../../../components/UI/Custom/LoadingCircle";
import { Notification as Toast } from "../../../../components/UI/Notifications";
import { handleError } from "../../../../utils/custom/errorHandler";

import { getLeadChatApi } from "../../../../utils/api/Chat/getLeadChatApi";
import { sendChatMessageApi } from "../../../../utils/api/Chat/sendChatMessageApi";
import { deleteChatMessageApi } from "../../../../utils/api/Chat/deleteChatMessageApi";

const translations = {
    ru: {
        empty: "Сообщений пока нет.",
        loadOlder: "Загрузить старые",
        placeholder: "Напишите сообщение...",
        send: "Отправить",
        sending: "Отправка...",
        delete: "Удалить сообщение",
        deleteConfirm: "Удалить это сообщение?",
    },
    en: {
        empty: "No messages yet.",
        loadOlder: "Load older",
        placeholder: "Type message...",
        send: "Send",
        sending: "Sending...",
        delete: "Delete message",
        deleteConfirm: "Delete this message?",
    },
};

function normalizeAsc(items) {
    const arr = Array.isArray(items) ? [...items] : [];
    arr.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
    return arr;
}

export const LeadChatPage = ({ role = "admin" }) => {
    const { id } = useParams();
    const leadId = useMemo(() => Number(id), [id]);

    const { theme } = useContext(ThemeContext);
    const { langSelected } = useContext(LangContext);
    const t = translations[langSelected] || translations.ru;

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);

    const [chat, setChat] = useState(null);
    const [messages, setMessages] = useState([]);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [text, setText] = useState("");
    const [menu, setMenu] = useState({ open: false, x: 0, y: 0, messageId: null });

    const latestIdRef = useRef(0);
    const refreshInFlightRef = useRef(false);

    const listRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);

    const scrollToBottom = useCallback((smooth = false) => {
        const el = listRef.current;
        if (!el) return;

        el.scrollTo({
            top: el.scrollHeight,
            behavior: smooth ? "smooth" : "auto",
        });
    }, []);

    const autoResizeTextarea = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, []);

    useEffect(() => {
        autoResizeTextarea();
    }, [text, autoResizeTextarea]);

    const mergeMessages = useCallback((incoming) => {
        if (!incoming || !incoming.id) return;

        setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            if (seen.has(incoming.id)) return prev;

            const merged = [...prev, incoming];
            merged.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
            return merged;
        });

        latestIdRef.current = Math.max(latestIdRef.current || 0, Number(incoming.id) || 0);
    }, []);

    const loadPage = useCallback(
        async (pageToLoad, { append = false } = {}) => {
            const res = await getLeadChatApi(leadId, role, pageToLoad, 50);

            setChat(res.chat);
            setTotalPages(res.meta?.total_pages || 1);

            const asc = normalizeAsc(res.data);

            if (!append) {
                setMessages(asc);
                const lastId = asc.length ? asc[asc.length - 1].id : 0;
                latestIdRef.current = Number(lastId) || 0;
                setPage(pageToLoad);
                return;
            }

            setMessages((prev) => {
                const seen = new Set(prev.map((m) => m.id));
                const merged = [...prev, ...asc.filter((m) => !seen.has(m.id))];
                merged.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
                return merged;
            });

            const lastId = asc.length ? asc[asc.length - 1].id : 0;
            latestIdRef.current = Math.max(latestIdRef.current || 0, Number(lastId) || 0);
        },
        [leadId, role]
    );

    const loadInitial = useCallback(async () => {
        setIsLoading(true);
        setErrors({});

        try {
            await loadPage(1, { append: false });
            setTimeout(() => scrollToBottom(false), 0);
        } catch (e) {
            handleError(e?.response?.data || e, setErrors);
            setMessages([]);
            setTotalPages(1);
            latestIdRef.current = 0;
        } finally {
            setIsLoading(false);
        }
    }, [loadPage, scrollToBottom]);

    const loadOlder = useCallback(async () => {
        if (page >= totalPages) return;

        const nextPage = page + 1;

        try {
            const res = await getLeadChatApi(leadId, role, nextPage, 50);

            setChat(res.chat);
            setTotalPages(res.meta?.total_pages || totalPages);

            const olderAsc = normalizeAsc(res.data);

            setMessages((prev) => {
                const seen = new Set(prev.map((m) => m.id));
                const merged = [...olderAsc.filter((m) => !seen.has(m.id)), ...prev];
                merged.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
                return merged;
            });

            setPage(nextPage);
        } catch (e) {
            handleError(e?.response?.data || e, setErrors);
        }
    }, [page, totalPages, leadId, role]);

    const refreshLatest = useCallback(async () => {
        // fetch only when needed (ws event fallback)
        const res = await getLeadChatApi(leadId, role, 1, 50);

        setChat(res.chat);
        setTotalPages(res.meta?.total_pages || 1);

        const asc = normalizeAsc(res.data);
        const latestId = latestIdRef.current || 0;

        const newOnes = asc.filter((m) => (Number(m?.id) || 0) > latestId);
        if (!newOnes.length) return;

        setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const merged = [...prev, ...newOnes.filter((m) => !seen.has(m.id))];
            merged.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
            return merged;
        });

        latestIdRef.current = Number(newOnes[newOnes.length - 1]?.id) || latestId;
        scrollToBottom(true);
    }, [leadId, role, scrollToBottom]);

    const send = useCallback(async () => {
        const trimmed = text.trim();
        if (!trimmed || isSending) return;

        setIsSending(true);
        setErrors({});

        const optimisticId = Date.now() * 1000;
        const optimistic = {
            id: optimisticId,
            sender_type: "manager",
            sender_id: null,
            message: trimmed,
            created_at: new Date().toISOString(),
            _optimistic: true,
        };

        setMessages((prev) => {
            const merged = [...prev, optimistic];
            merged.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
            return merged;
        });

        setText("");
        setTimeout(() => {
            autoResizeTextarea();
            scrollToBottom(true);
        }, 0);

        try {
            const created = await sendChatMessageApi(leadId, role, trimmed);

            if (!created || !created.id || !created.message) {
                setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
                await loadPage(1, { append: false });
                setTimeout(() => scrollToBottom(true), 0);
                return;
            }

            setMessages((prev) => {
                const replaced = prev.map((m) => (m.id === optimisticId ? created : m));
                const seen = new Set();
                const dedup = [];
                for (const m of replaced) {
                    if (!m?.id || seen.has(m.id)) continue;
                    seen.add(m.id);
                    dedup.push(m);
                }
                dedup.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
                return dedup;
            });

            latestIdRef.current = Math.max(latestIdRef.current || 0, Number(created?.id) || 0);

            // sync (useful if backend formats created_at / attaches sender info)
            await loadPage(1, { append: true });

            setTimeout(() => scrollToBottom(true), 0);
        } catch (e) {
            setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
            handleError(e?.response?.data || e, setErrors);
        } finally {
            setIsSending(false);
        }
    }, [text, isSending, leadId, role, autoResizeTextarea, scrollToBottom, loadPage]);

    const closeMenu = useCallback(() => {
        setMenu((prev) => ({ ...prev, open: false, messageId: null }));
    }, []);

    const onMessageContextMenu = useCallback((event, messageId) => {
        if (role !== "admin") return;
        event.preventDefault();
        setMenu({
            open: true,
            x: event.clientX,
            y: event.clientY,
            messageId: Number(messageId) || null,
        });
    }, [role]);

    const deleteMessage = useCallback(async () => {
        const messageId = Number(menu.messageId || 0);
        if (!messageId || !leadId) {
            closeMenu();
            return;
        }

        closeMenu();

        if (!window.confirm(t.deleteConfirm)) {
            return;
        }

        try {
            await deleteChatMessageApi(leadId, role, messageId);
            setMessages((prev) => prev.filter((item) => Number(item?.id) !== messageId));
        } catch (e) {
            handleError(e?.response?.data || e, setErrors);
        }
    }, [menu.messageId, leadId, role, closeMenu, t.deleteConfirm]);

    useEffect(() => {
        if (!leadId) return;
        loadInitial();
    }, [leadId, loadInitial]);

    // ✅ realtime: react to already-established WS notifications via browser event
    useEffect(() => {
        if (!leadId) return;

        const handler = (evt) => {
            const n = evt?.detail;
            if (!n) return;

            const action = String(n?.action || "").toLowerCase();
            if (action !== "chat.message.from_lead") return;

            const notifLeadId = Number(n?.payload?.chat?.lead_id || 0);
            if (notifLeadId !== leadId) return;

            // If notification includes full message data — append directly (no API call)
            const messageId = Number(n?.payload?.chat?.message_id || 0);
            const textFromPayload =
                n?.payload?.text ||
                n?.payload?.message ||
                n?.payload?.chat?.text ||
                n?.payload?.chat_message?.message ||
                null;

            if (messageId > 0 && typeof textFromPayload === "string" && textFromPayload.trim()) {
                mergeMessages({
                    id: messageId,
                    sender_type: "lead",
                    sender_id: null,
                    message: textFromPayload,
                    created_at: n?.payload?.created_at || n?.created_at || new Date().toISOString(),
                });

                scrollToBottom(true);
                return;
            }

            // Fallback: fetch latest (still NOT polling)
            if (refreshInFlightRef.current) return;
            refreshInFlightRef.current = true;

            Promise.resolve(refreshLatest())
                .finally(() => {
                    refreshInFlightRef.current = false;
                });
        };

        window.addEventListener("crm.notification.created", handler);
        return () => window.removeEventListener("crm.notification.created", handler);
    }, [leadId, refreshLatest, mergeMessages, scrollToBottom]);

    useEffect(() => {
        if (!menu.open) return;

        const closeOnOutside = (event) => {
            const menuNode = menuRef.current;
            if (menuNode && !menuNode.contains(event.target)) {
                closeMenu();
            }
        };

        const closeOnEsc = (event) => {
            if (event.key === "Escape") {
                closeMenu();
            }
        };

        window.addEventListener("click", closeOnOutside, true);
        window.addEventListener("contextmenu", closeOnOutside, true);
        window.addEventListener("keydown", closeOnEsc, true);
        window.addEventListener("scroll", closeMenu, true);

        return () => {
            window.removeEventListener("click", closeOnOutside, true);
            window.removeEventListener("contextmenu", closeOnOutside, true);
            window.removeEventListener("keydown", closeOnEsc, true);
            window.removeEventListener("scroll", closeMenu, true);
        };
    }, [menu.open, closeMenu]);

    const containerClass = `${styles.container} ${theme === "dark" ? styles.darkContainer : ""}`;
    const contentClass = `${styles.content} ${theme === "dark" ? styles.darkContent : ""}`;

    return (
        <div className={containerClass}>
            {errors.general && <Toast message={errors.general} type={"error"} />}

            <div className={contentClass}>
                {isLoading ? (
                    <div className={styles.loadingBlock}>
                        <LoadingCircle size={30} thickness={1} />
                    </div>
                ) : (
                    <>
                        <div className={styles.list} ref={listRef}>
                            {page < totalPages ? (
                                <button
                                    className={`${styles.loadOlderBtn} ${theme === "dark" ? styles.darkLoadOlderBtn : ""}`}
                                    onClick={loadOlder}
                                >
                                    {t.loadOlder}
                                </button>
                            ) : null}

                            {messages.length === 0 ? (
                                <div className={`${styles.empty} ${theme === "dark" ? styles.darkEmpty : ""}`}>
                                    {t.empty}
                                </div>
                            ) : (
                                messages.map((m) => {
                                    const isMine = String(m?.sender_type || "") === "manager";
                                    return (
                                        <div
                                            key={m.id}
                                            className={[
                                                styles.item,
                                                isMine ? styles.itemMine : styles.itemLead,
                                                theme === "dark" ? styles.darkItem : "",
                                            ].join(" ")}
                                            onContextMenu={(e) => onMessageContextMenu(e, m.id)}
                                        >
                                            <div className={styles.msg}>{m.message}</div>
                                            <div className={styles.time}>{m.created_at ? m.created_at : ""}</div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {menu.open ? (
                            <div
                                ref={menuRef}
                                className={`${styles.contextMenu} ${theme === "dark" ? styles.darkContextMenu : ""}`}
                                style={{ left: `${menu.x}px`, top: `${menu.y}px` }}
                            >
                                <button
                                    type="button"
                                    className={`${styles.contextMenuItem} ${styles.contextMenuDelete}`}
                                    onClick={deleteMessage}
                                >
                                    {t.delete}
                                </button>
                            </div>
                        ) : null}

                        <div className={styles.composer}>
              <textarea
                  ref={inputRef}
                  className={`${styles.input} ${theme === "dark" ? styles.darkInput : ""}`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t.placeholder}
                  rows={1}
                  onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          send();
                      }
                  }}
              />
                            <button
                                className={`${styles.sendBtn} ${theme === "dark" ? styles.darkSendBtn : ""}`}
                                onClick={send}
                                disabled={isSending || !text.trim()}
                            >
                                {isSending ? t.sending : t.send}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
